import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { extractArray } from '@/lib/deliverablesResponse';
import { LeadSource } from '@/types/lead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  Target,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Loader2,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

interface EditingSource {
  id: string;
  name: string;
  description: string;
  color: string;
}

export const LeadSourcesManager: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // State
  const [isAdding, setIsAdding] = useState(false);
  const [newSource, setNewSource] = useState({ name: '', description: '', color: '#6B7280' });
  const [editingSource, setEditingSource] = useState<EditingSource | null>(null);

  // Queries
  const { data: sources, isLoading } = useQuery({
    queryKey: ['lead-sources-all'],
    queryFn: async () => extractArray<LeadSource>(await api.getAllLeadSources()),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) =>
      api.createLeadSource(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources-all'] });
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
      setIsAdding(false);
      setNewSource({ name: '', description: '', color: '#6B7280' });
      toast({ title: 'Success', description: 'Lead source created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<LeadSource> }) =>
      api.updateLeadSource(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources-all'] });
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
      setEditingSource(null);
      toast({ title: 'Success', description: 'Lead source updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteLeadSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-sources-all'] });
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
      toast({ title: 'Success', description: 'Lead source deactivated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Handlers
  const handleCreate = () => {
    if (!newSource.name.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    createMutation.mutate({
      name: newSource.name.trim(),
      description: newSource.description.trim() || undefined,
      color: newSource.color,
    });
  };

  const handleUpdate = () => {
    if (!editingSource || !editingSource.name.trim()) {
      toast({ title: 'Error', description: 'Name is required', variant: 'destructive' });
      return;
    }
    updateMutation.mutate({
      id: editingSource.id,
      data: {
        name: editingSource.name.trim(),
        description: editingSource.description.trim() || undefined,
        color: editingSource.color,
      },
    });
  };

  const handleToggleActive = (source: LeadSource) => {
    updateMutation.mutate({
      id: source.id,
      data: { isActive: !source.isActive },
    });
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to deactivate this lead source? It will no longer appear in the dropdown for new leads.')) {
      deleteMutation.mutate(id);
    }
  };

  const startEditing = (source: LeadSource) => {
    setEditingSource({
      id: source.id,
      name: source.name,
      description: source.description || '',
      color: source.color || '#6B7280',
    });
  };

  const colorOptions = [
    { value: '#0077B5', label: 'LinkedIn Blue' },
    { value: '#10B981', label: 'Green' },
    { value: '#6366F1', label: 'Indigo' },
    { value: '#F59E0B', label: 'Amber' },
    { value: '#EF4444', label: 'Red' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#14B8A6', label: 'Teal' },
    { value: '#3B82F6', label: 'Blue' },
    { value: '#F97316', label: 'Orange' },
    { value: '#6B7280', label: 'Gray' },
  ];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-blue-600" />
          Lead Sources
        </CardTitle>
        <p className="text-sm text-gray-500">
          Manage the sources where your leads come from. These will appear as options when adding new leads.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add new source form */}
        {isAdding ? (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
            <div className="flex gap-3">
              <Input
                placeholder="Source name (e.g., LinkedIn)"
                value={newSource.name}
                onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                className="flex-1"
              />
              <select
                value={newSource.color}
                onChange={(e) => setNewSource({ ...newSource, color: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {colorOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <Input
              placeholder="Description (optional)"
              value={newSource.description}
              onChange={(e) => setNewSource({ ...newSource, description: e.target.value })}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setIsAdding(false)}>
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setIsAdding(true)}
            className="w-full border-dashed"
          >
            <Plus className="h-4 w-4 mr-2" /> Add New Source
          </Button>
        )}

        {/* Existing sources list */}
        <div className="space-y-2">
          {sources?.map((source) => (
            <div
              key={source.id}
              className={`p-3 rounded-lg border ${
                source.isActive
                  ? 'bg-white border-gray-200'
                  : 'bg-gray-50 border-gray-100 opacity-60'
              }`}
            >
              {editingSource?.id === source.id ? (
                // Editing mode
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={editingSource.name}
                      onChange={(e) =>
                        setEditingSource({ ...editingSource, name: e.target.value })
                      }
                      className="flex-1"
                    />
                    <select
                      value={editingSource.color}
                      onChange={(e) =>
                        setEditingSource({ ...editingSource, color: e.target.value })
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      {colorOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Input
                    placeholder="Description"
                    value={editingSource.description}
                    onChange={(e) =>
                      setEditingSource({ ...editingSource, description: e.target.value })
                    }
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingSource(null)}
                    >
                      <X className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleUpdate}
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-1" />
                      )}
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                // Display mode
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: source.color || '#6B7280' }}
                    />
                    <div>
                      <p className="font-medium text-gray-900">{source.name}</p>
                      {source.description && (
                        <p className="text-sm text-gray-500">{source.description}</p>
                      )}
                    </div>
                    {!source.isActive && (
                      <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(source)}
                      title={source.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {source.isActive ? (
                        <ToggleRight className="h-5 w-5 text-green-600" />
                      ) : (
                        <ToggleLeft className="h-5 w-5 text-gray-400" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => startEditing(source)}
                    >
                      <Pencil className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(source.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {sources?.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              No lead sources configured. Add your first source above.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
